/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import classNames from 'classnames';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useEffect, useRef, useState } from 'react';
import { useTab } from './useTab';
import Button from '$/components/Button';

export const TabVIewerDesktop = ({ tabs }: { tabs: string[] }) => {
  const { onSetNewActiveTab, activeTab, version } = useTab(tabs);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerRef = useRef<any>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [visibleCount, setVisibleCount] = useState(tabs?.length ?? 0);
  const [hiddenTabs, setHiddenTabs] = useState<string[]>([]);
  const [isOpenHiddenTabs, setIsOpenHiddenTabs] = useState(false);

  const updateVisibleTabs = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!tabs?.length) {
      return null;
    }

    const containerWidth = container.offsetWidth;
    const children = Array.from(container.children);
    let totalWidth = 0;
    let count = 0;
    const gap = 8;
    const moreButtonWith = 40;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      totalWidth += child.offsetWidth + gap;
      if (totalWidth + moreButtonWith > containerWidth) {
        break;
      }
      count++;
    }
    const needsMoreButton = count < tabs.length;
    if (needsMoreButton) {
      let adjustedWidth = 0;
      for (let i = 0; i < count; i++) {
        adjustedWidth += (children[i] as HTMLElement).offsetWidth;
      }

      if (adjustedWidth + moreButtonWith > containerWidth) count--;
      setVisibleCount(count);
      tabs?.length ? setHiddenTabs(tabs.slice(count)) : setHiddenTabs([]);
    }
  };
  const debouncedUpdateVisibleTabs = useDebouncedCallback(
    updateVisibleTabs,
    100,
  );
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(event.target as Node)
      ) {
        setIsOpenHiddenTabs(false);
      }
    };

    if (isOpenHiddenTabs) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenHiddenTabs]);

  useEffect(() => {
    debouncedUpdateVisibleTabs();
    window.addEventListener('resize', debouncedUpdateVisibleTabs);
    return () =>
      window.removeEventListener('resize', debouncedUpdateVisibleTabs);
  }, [tabs, debouncedUpdateVisibleTabs]);

  if (version.mobile) {
    return null;
  }
  return (
    <div
      ref={containerRef}
      className="flex flex-row gap-2 md:max-w-general/narrow-content-max-width-3"
    >
      {tabs.slice(0, visibleCount).map((tab, index) => {
        return (
          <Button
            // eslint-disable-next-line react/no-array-index-key
            key={`${tab}-${index}`}
            styleType="neutral-light-and-blue"
            variant="outline"
            selected={
              index + 1 === +(activeTab ?? 0) || (!activeTab && index === 0)
            }
            className="justify-start"
            title={tab}
            onClick={() => onSetNewActiveTab(index)}
          >
            <div className="overflow-hidden text-ellipsis">{tab}</div>
          </Button>
        );
      })}
      {hiddenTabs.length > 0 && (
        <div className="relative">
          <Button
            ref={moreButtonRef as any}
            onClick={() => setIsOpenHiddenTabs((prev) => !prev)}
            className="w-10 px-0"
            selected={!!(activeTab && +activeTab > visibleCount)}
            styleType="neutral-light-and-blue"
            variant="outline"
          >
            ...
            {isOpenHiddenTabs ? (
              <div
                ref={menuRef}
                className="absolute right-0 top-full mt-1 rounded border shadow-lg bg-white"
              >
                {hiddenTabs.map((tab, index) => (
                  <div
                    key={`${tab}-hidden`}
                    className={classNames(
                      'cursor-pointer whitespace-nowrap px-4 py-2 text-right hover:bg-gray-100',
                      {
                        'mhg-alpha-btn-outline-neutral-light-and-blue mhg-alpha-btn-selected':
                          !!activeTab &&
                          +activeTab >= visibleCount &&
                          +activeTab - visibleCount === index + 1,
                      },
                    )}
                    onClick={() => onSetNewActiveTab(visibleCount + index)}
                  >
                    {tab}
                  </div>
                ))}
              </div>
            ) : null}
          </Button>
        </div>
      )}
    </div>
  );
};
